import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { handleError } from 'src/analytics/KatalErrorLogger';
import { logger } from 'src/analytics/KatalLogger';
import * as api from 'src/api/app-sync-services';
import { campaignDataReadS3URI, parseCampaignData } from 'src/features/business-group/campaigns/utils/CampaignUtils';
import { Status } from 'src/models/AuthContextModels';
import {
  CampaignDimensionMetadata,
  CampaignsMetadata,
  CountryCompanyMapping,
  DMMProductLineCampaign,
  DMMProductLineCampaignFlatten
} from 'src/models/DmmModel';
import { getFileFromS3URI } from 'src/utils/aws-s3-services';

// Types
interface StateWithStatus<T> {
  data: T[];
  status: Status;
  error: string | { message: string } | null;
}

export interface CampaignFilters {
  selectedProductLineCode: string | null;
  selectedCampaigns: string[];
  selectedCountryCodes: string[];
}

export interface DMMState {
  campaignsMetadata: StateWithStatus<CampaignsMetadata>;
  campaignDimensions: StateWithStatus<CampaignDimensionMetadata>;
  countryCompanyMapping: StateWithStatus<CountryCompanyMapping>;
  campaigns: StateWithStatus<DMMProductLineCampaignFlatten>;
  campaignFilters: CampaignFilters;
}

// Helper Functions
const createInitialStateWithStatus = {
  data: [],
  status: Status.NotInitiated,
  error: null
};

// Initial State
const initialState: DMMState = {
  campaignsMetadata: createInitialStateWithStatus,
  campaignDimensions: createInitialStateWithStatus,
  countryCompanyMapping: createInitialStateWithStatus,
  campaigns: createInitialStateWithStatus,
  campaignFilters: {
    selectedProductLineCode: null,
    selectedCampaigns: [],
    selectedCountryCodes: []
  }
};

// Thunks
// Campaigns Metadata
export const fetchCampaignMetadata = createAsyncThunk('dmm/fetchCampaignMetadata', async (_, { rejectWithValue }) => {
  try {
    return await api.getCampaignMetadata();
  } catch (error) {
    return rejectWithValue('Failed to fetch Campaign Metadata');
  }
});

// Campaign Dimensions
export const fetchCampaignDimensions = createAsyncThunk('dmm/fetchCampaignDimensions', async (_, { rejectWithValue }) => {
  try {
    return await api.getCampaignDimensionsMetadata();
  } catch (error) {
    return rejectWithValue('Failed to fetch Campaign Dimensions');
  }
});

// Country Company Mapping
export const fetchCountryCompanyMapping = createAsyncThunk('dmm/fetchCountryCompanyMapping', async (_, { rejectWithValue }) => {
  try {
    return await api.getCountryCompanyMapping();
  } catch (error) {
    return rejectWithValue('Failed to fetch Country & Company Mappings');
  }
});

// All Campaigns belongs to all Product
export const fetchDMMCampaignDetails = createAsyncThunk(
  'dmm/fetchDMMCampaignDetails',
  async (params: { dataClassificationId: number; businessGroupShortDesc: string }, { rejectWithValue }) => {
    try {
      const { dataClassificationId, businessGroupShortDesc } = params;
      const s3URI = campaignDataReadS3URI(dataClassificationId, businessGroupShortDesc);
      const campaignsRowDataResponseFromS3 = (await getFileFromS3URI(s3URI)) as unknown as DMMProductLineCampaign[];
      const parsedCampaigns = parseCampaignData(campaignsRowDataResponseFromS3) as DMMProductLineCampaignFlatten[];
      logger.info(`Fetched Campaigns from S3 and parsed: ${parsedCampaigns.length}`);
      return parsedCampaigns;
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to fetch DMM Campaign Details';
      handleError(error, 'Error fetching campaign details', undefined);
      return rejectWithValue(errorMessage);
    }
  }
);

// Slice
const dmmSlice = createSlice({
  name: 'dmm',
  initialState,
  reducers: {
    setProductLineFilters: (state, action: PayloadAction<string | null>) => {
      state.campaignFilters.selectedProductLineCode = action.payload;
    },
    setSelectedCampaigns: (state, action: PayloadAction<string[]>) => {
      state.campaignFilters.selectedCampaigns = action.payload;
    },
    setSelectedCountryCodes: (state, action: PayloadAction<string[]>) => {
      state.campaignFilters.selectedCountryCodes = action.payload;
    },
    resetSecondaryFilters: (state) => {
      state.campaignFilters.selectedCampaigns = [];
      state.campaignFilters.selectedCountryCodes = [];
    }
  },
  extraReducers: (builder) => {
    // Helper function to handle async actions
    const addAsyncCases = <T>(thunk: any, stateKey: keyof DMMState, options: { resetFilters?: boolean } = {}) => {
      builder
        .addCase(thunk.fulfilled, (state, action) => {
          const slice = state[stateKey] as StateWithStatus<T>;
          slice.data = action.payload;
          slice.status = Status.Completed;
          slice.error = null;

          if (options.resetFilters) {
            state.campaignFilters.selectedCampaigns = [];
            state.campaignFilters.selectedCountryCodes = [];
          }
        })
        .addCase(thunk.rejected, (state, action) => {
          const slice = state[stateKey] as StateWithStatus<T>;
          slice.data = [];
          slice.status = Status.Failed;
          slice.error = action.payload || action.error.message || 'An unknown error occurred';

          if (options.resetFilters) {
            state.campaignFilters.selectedCampaigns = [];
            state.campaignFilters.selectedCountryCodes = [];
          }
        });
    };

    // Add cases for each async action
    addAsyncCases(fetchCampaignMetadata, 'campaignsMetadata');
    addAsyncCases(fetchDMMCampaignDetails, 'campaigns', { resetFilters: true });
    addAsyncCases(fetchCampaignDimensions, 'campaignDimensions');
    addAsyncCases(fetchCountryCompanyMapping, 'countryCompanyMapping');
  }
});

export const { setProductLineFilters, setSelectedCampaigns, setSelectedCountryCodes, resetSecondaryFilters } = dmmSlice.actions;

export default dmmSlice.reducer;
